// Renders a ticket's uniqueCode as a scannable QR image.
//
// This exists because printing cannot be hardware-verified until the totems
// with the Sewoo printer arrive: during payment testing this IS the
// visitor's ticket. It stays afterwards as the fallback for paper-out and
// printer-error cases.
//
// The web app generates QR server-side via @pdfme/schemas, which is not
// reusable in the browser, so the kiosk carries its own renderer.

import QRCode from "qrcode";
import { useEffect, useState } from "react";
import { useLang } from "../i18n/LangContext";

const QR_MARGIN = 1;

export function TicketQr({
  uniqueCode,
  size,
}: {
  uniqueCode: string;
  size: number;
}) {
  const { t } = useLang();
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(uniqueCode, {
      width: size,
      margin: QR_MARGIN,
      errorCorrectionLevel: "M",
    })
      .then((url) => {
        if (!cancelled) {
          setDataUrl(url);
        }
      })
      .catch(() => {
        // A QR that fails to render must not break the done screen; the
        // ticket number below it is still readable.
      });
    return () => {
      cancelled = true;
    };
  }, [uniqueCode, size]);

  if (!dataUrl) {
    return <div className="ticket-qr ticket-qr--pending" style={{ width: size, height: size }} />;
  }

  return (
    <img
      alt={t("ticketQrLabel", { code: uniqueCode })}
      className="ticket-qr"
      height={size}
      src={dataUrl}
      width={size}
    />
  );
}
